var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import RcDrawer from 'rc-drawer';
// import CloseOutlined from '@ant-design/icons/CloseOutlined';
import classNames from 'classnames';
import throttle from 'lodash/throttle';
import { ConfigContext } from '../config-provider';
import { tuple } from '../_util/type';
import useForceUpdate from '../_util/hooks/useForceUpdate';
import LocaleReceiver from '../locale-provider/LocaleReceiver';
import { getConfirmLocale } from '../modal/locale';
import Button from '../button';
import PopupClose from '../_util/bonreeIcons/PopupClose';
import Popover from '../popover';
const DrawerContext = React.createContext(null);
const PlacementTypes = tuple('top', 'right', 'bottom', 'left');
const SizeTypes = tuple('default', 'large');
const defaultPushState = { distance: 180 };
const Drawer = React.forwardRef((_a, ref) => {
    var { width = 600, height = 256, size = 'default', closable = true, placement = 'right', maskClosable = true, mask = true, level = null, keyboard = true, push = defaultPushState, closeIcon = <PopupClose />, bodyStyle, drawerStyle, prefixCls, className, direction, visible, children, zIndex, destroyOnClose, style, title, headerStyle, onOk, onClose, footer, footerStyle, confirmLoading = false, extra, okText, cancelText, okDisabled, okButtonPopoverContent } = _a, 
    // scrollbarGap = true,
    rest = __rest(_a, ["width", "height", "size", "closable", "placement", "maskClosable", "mask", "level", "keyboard", "push", "closeIcon", "bodyStyle", "drawerStyle", "prefixCls", "className", "direction", "visible", "children", "zIndex", "destroyOnClose", "style", "title", "headerStyle", "onOk", "onClose", "footer", "footerStyle", "confirmLoading", "extra", "okText", "cancelText", "okDisabled", "okButtonPopoverContent"]);
    const forceUpdate = useForceUpdate();
    const [internalPush, setPush] = React.useState(false);
    const parentDrawer = React.useContext(DrawerContext);
    const destroyClose = React.useRef(false);
    const darwerHeaderRef = React.useRef(null);
    const darwerBodyRef = React.useRef(null);
    const darwerFooterRef = React.useRef(null);
    const darwerCustomFooterRef = React.useRef(null);
    React.useEffect(() => {
        // fix: delete drawer in child and re-render, no push started.
        // <Drawer>{show && <Drawer />}</Drawer>
        if (visible && parentDrawer) {
            parentDrawer.push();
        }
        return () => {
            if (parentDrawer) {
                parentDrawer.pull();
                // parentDrawer = null;
            }
        };
    }, []);
    React.useEffect(() => {
        if (parentDrawer) {
            if (visible) {
                parentDrawer.push();
            }
            else {
                parentDrawer.pull();
            }
        }
    }, [visible]);
    // 控制header footer阴影
    const triggerShadow = throttle(e => {
        var _a, _b, _c, _d, _e, _f;
        const { scrollHeight, scrollTop, clientHeight } = e.target;
        if (scrollTop > 0) {
            (_a = darwerHeaderRef.current) === null || _a === void 0 ? void 0 : _a.classList.add('hasShadow');
        }
        else {
            (_b = darwerHeaderRef.current) === null || _b === void 0 ? void 0 : _b.classList.remove('hasShadow');
        }
        // dom的实际宽高会带小数，但scrollHeight、clientHeight、scrollTop会向下取整，导致计算误差，这里多加1px，消除误差
        if (scrollHeight > clientHeight + scrollTop + 1) {
            (_c = darwerFooterRef.current) === null || _c === void 0 ? void 0 : _c.classList.add('hasShadow');
            (_d = darwerCustomFooterRef.current) === null || _d === void 0 ? void 0 : _d.classList.add(`${prefixCls}-footer-custom-shadow`);
        }
        else {
            (_e = darwerFooterRef.current) === null || _e === void 0 ? void 0 : _e.classList.remove('hasShadow');
            (_f = darwerCustomFooterRef.current) === null || _f === void 0 ? void 0 : _f.classList.remove(`${prefixCls}-footer-custom-shadow`);
        }
    }, 200);
    // 监听滚动状态
    const monitorScrollEvent = () => {
        // 抽屉内容滚动时设置抽屉标题增加阴影
        if (darwerBodyRef.current) {
            darwerBodyRef.current.addEventListener('scroll', triggerShadow);
        }
    };
    // 监听body高度
    const mintiorBodyHight = () => {
        var _a, _b, _c, _d;
        if (darwerBodyRef.current) {
            const { scrollHeight, clientHeight } = darwerBodyRef.current;
            if (scrollHeight > clientHeight) {
                (_a = darwerFooterRef.current) === null || _a === void 0 ? void 0 : _a.classList.add('hasShadow');
                (_b = darwerCustomFooterRef.current) === null || _b === void 0 ? void 0 : _b.classList.add(`${prefixCls}-footer-custom-shadow`);
            }
            else {
                (_c = darwerFooterRef.current) === null || _c === void 0 ? void 0 : _c.classList.remove('hasShadow');
                (_d = darwerCustomFooterRef.current) === null || _d === void 0 ? void 0 : _d.classList.remove(`${prefixCls}-footer-custom-shadow`);
            }
        }
    };
    // 处理浏览器窗口大小改变
    const handleResize = throttle(() => {
        mintiorBodyHight();
    }, 200);
    React.useEffect(() => {
        let observer = null;
        if (visible) {
            if (darwerBodyRef.current) {
                window.addEventListener('resize', handleResize);
                monitorScrollEvent();
                mintiorBodyHight();
                observer = new MutationObserver(() => {
                    mintiorBodyHight();
                });
                observer.observe(darwerBodyRef.current, { childList: true, subtree: true });
            }
        }
        return () => {
            var _a;
            (_a = darwerBodyRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('scroll', triggerShadow);
            window.removeEventListener('resize', handleResize);
            observer === null || observer === void 0 ? void 0 : observer.disconnect();
        };
    }, [visible]);
    const operations = React.useMemo(() => ({
        push() {
            if (push) {
                setPush(true);
            }
        },
        pull() {
            if (push) {
                setPush(false);
            }
        },
    }), [push]);
    React.useImperativeHandle(ref, () => operations, [operations]);
    const isDestroyOnClose = destroyOnClose && !visible;
    const onDestroyTransitionEnd = () => {
        if (!isDestroyOnClose) {
            return;
        }
        if (!visible) {
            destroyClose.current = true;
            forceUpdate();
        }
    };
    const getOffsetStyle = () => {
        // https://github.com/ant-design/ant-design/issues/24287
        if (!visible && !mask) {
            return {};
        }
        const offsetStyle = {};
        if (placement === 'left' || placement === 'right') {
            const defaultWidth = size === 'large' ? 736 : 378;
            offsetStyle.width = typeof width === 'undefined' ? defaultWidth : width;
        }
        else {
            const defaultHeight = size === 'large' ? 736 : 378;
            offsetStyle.height = typeof height === 'undefined' ? defaultHeight : height;
        }
        return offsetStyle;
    };
    const getRcDrawerStyle = () => {
        // get drawer push width or height
        const getPushTransform = (_placement) => {
            let distance;
            if (typeof push === 'boolean') {
                distance = push ? defaultPushState.distance : 0;
            }
            else {
                distance = push.distance;
            }
            distance = parseFloat(String(distance || 0));
            if (_placement === 'left' || _placement === 'right') {
                return `translateX(${_placement === 'left' ? distance : -distance}px)`;
            }
            if (_placement === 'top' || _placement === 'bottom') {
                return `translateY(${_placement === 'top' ? distance : -distance}px)`;
            }
        };
        // 当无 mask 时，将 width 应用到外层容器上
        // 解决 https://github.com/ant-design/ant-design/issues/12401 的问题
        const offsetStyle = mask ? {} : getOffsetStyle();
        return Object.assign(Object.assign({ zIndex, transform: internalPush ? getPushTransform(placement) : undefined }, offsetStyle), style);
    };
    const closeIconNode = closable && (<button type="button" onClick={onClose} aria-label="Close" className={`${prefixCls}-close`}>
        {closeIcon}
      </button>);
    function renderHeader() {
        if (!title && !closable) {
            return null;
        }
        return (<div ref={darwerHeaderRef} className={classNames(`${prefixCls}-header`, {
                [`${prefixCls}-header-close-only`]: closable && !title && !extra,
            })} style={headerStyle}>
          <div className={`${prefixCls}-header-title`}>
            {closeIconNode}
            {title && <div className={`${prefixCls}-title`}>{title}</div>}
          </div>
          {extra && <div className={`${prefixCls}-extra`}>{extra}</div>}
        </div>);
    }
    function renderFooter(locale) {
        const okButtonShowPopover = typeof okButtonPopoverContent === 'number' ||
            typeof okButtonPopoverContent === 'string' ||
            React.isValidElement(okButtonPopoverContent);
        const footerClassName = `${prefixCls}-footer`;
        const handleCancel = (e) => {
            onClose === null || onClose === void 0 ? void 0 : onClose(e);
        };
        const handleOk = (e) => {
            onOk === null || onOk === void 0 ? void 0 : onOk(e);
        };
        return (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {footer === undefined ? (<div ref={darwerFooterRef} className={footerClassName} style={footerStyle}>
              {okButtonShowPopover ? (<Popover content={okButtonPopoverContent}>
                  <Button type="primary" onClick={handleOk} loading={confirmLoading} disabled={okDisabled}>
                    {okText || locale.okText}
                  </Button>
                </Popover>) : (<Button type="primary" onClick={handleOk} loading={confirmLoading} disabled={okDisabled}>
                  {okText || locale.okText}
                </Button>)}

              <Button onClick={handleCancel}>{cancelText || locale.cancelText}</Button>
            </div>) : (<div ref={darwerCustomFooterRef}>{footer}</div>)}
        </>);
    }
    // render drawer body dom
    const renderBody = () => {
        if (destroyClose.current && !visible) {
            return null;
        }
        destroyClose.current = false;
        const containerStyle = {};
        if (isDestroyOnClose) {
            // Increase the opacity transition, delete children after closing.
            containerStyle.opacity = 0;
            containerStyle.transition = 'opacity .3s';
        }
        return (<div className={`${prefixCls}-wrapper-body`} style={Object.assign(Object.assign({}, containerStyle), drawerStyle)} onTransitionEnd={onDestroyTransitionEnd}>
          {renderHeader()}
          <div ref={darwerBodyRef} className={`${prefixCls}-body`} style={bodyStyle}>
            {children}
          </div>
          <LocaleReceiver componentName="Modal" defaultLocale={getConfirmLocale()}>
            {renderFooter}
          </LocaleReceiver>
        </div>);
    };
    const drawerClassName = classNames({
        'no-mask': !mask,
        [`${prefixCls}-rtl`]: direction === 'rtl',
    }, className);
    const offsetStyle = mask ? getOffsetStyle() : {};
    return (<DrawerContext.Provider value={operations}>
        <RcDrawer handler={false} {...Object.assign({ placement,
        prefixCls,
        maskClosable,
        level,
        keyboard,
        children,
        onClose }, rest)} {...offsetStyle} open={visible} showMask={mask} style={getRcDrawerStyle()} className={drawerClassName}>
          {renderBody()}
        </RcDrawer>
      </DrawerContext.Provider>);
});
Drawer.displayName = 'Drawer';
const DrawerWrapper = React.forwardRef((props, ref) => {
    const { prefixCls: customizePrefixCls, getContainer: customizeGetContainer } = props;
    const { getPopupContainer, getPrefixCls, direction } = React.useContext(ConfigContext);
    const prefixCls = getPrefixCls('drawer', customizePrefixCls);
    const getContainer = 
    // 有可能为 false，所以不能直接判断
    customizeGetContainer === undefined && getPopupContainer
        ? () => getPopupContainer(document.body)
        : customizeGetContainer;
    return (<Drawer {...props} ref={ref} prefixCls={prefixCls} getContainer={getContainer} direction={direction}/>);
});
DrawerWrapper.displayName = 'DrawerWrapper';
export default DrawerWrapper;
